/**
 * Client-side proximity helpers for the events list when `near_zip` is active.
 * Mirrors the backend sort so native + Mobilize cards stay interleaved by
 * distance after the hook merges them.
 */

function toFiniteNumber(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

export function distanceMilesFromAnchor(event, anchor) {
  if (!anchor) return null;
  const lat = toFiniteNumber(event?.latitude);
  const lng = toFiniteNumber(event?.longitude);
  const anchorLat = toFiniteNumber(anchor.latitude);
  const anchorLng = toFiniteNumber(anchor.longitude);
  if (lat === null || lng === null || anchorLat === null || anchorLng === null) {
    return null;
  }

  const R = 3959;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat - anchorLat);
  const dLng = toRad(lng - anchorLng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(anchorLat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function sortEventsByNearZip(events, anchor) {
  if (!anchor || !Array.isArray(events)) return events;

  return [...events].sort((a, b) => {
    const distA = distanceMilesFromAnchor(a, anchor);
    const distB = distanceMilesFromAnchor(b, anchor);
    const bucketA = distA === null ? 1 : 0;
    const bucketB = distB === null ? 1 : 0;
    if (bucketA !== bucketB) return bucketA - bucketB;
    if (distA !== null && distB !== null && distA !== distB) return distA - distB;
    return new Date(a.start_datetime || 0) - new Date(b.start_datetime || 0);
  });
}

export function buildNearZipEventsPath(zipcode, stateAbbr = '') {
  const zip = String(zipcode || '').trim();
  if (!/^\d{5}/.test(zip)) return '/events';
  const qs = new URLSearchParams({ near_zip: zip.slice(0, 5) });
  if (stateAbbr) qs.set('state', stateAbbr);
  return `/events?${qs.toString()}`;
}

/** Dashboard link target: ZIP proximity when available, otherwise state-scoped list. */
export function buildNearbyEventsPath(zipcode, stateAbbr = '') {
  const zip = String(zipcode || '').trim();
  if (/^\d{5}/.test(zip)) {
    return buildNearZipEventsPath(zip.slice(0, 5), stateAbbr);
  }
  const state = String(stateAbbr || '').trim();
  if (state && state !== 'N/A') {
    return `/events?${new URLSearchParams({ state }).toString()}`;
  }
  return null;
}

export function formatNearZipLocationLabel(zipcode, stateAbbr = '') {
  const zip = String(zipcode || '').trim().slice(0, 5);
  if (!zip) return '';
  return stateAbbr ? `${zip}, ${stateAbbr}` : zip;
}
